import type { AudioEngine } from "../AudioEngine";
import type { BeatStep, ProceduralBeat } from "../ProceduralBeat";
import type { Composer } from "../composer/Composer";
import type { BarContext, MotifNote, SectionKind } from "../composer/types";
import { SCALES } from "./MusicalScale";

// Melodic-dubstep / future-bass lead. Plays the song's motif notes
// (one per 16th step, supplied by the Composer) through a saw + square
// blend with a snappy envelope, then through a multi-tap "delay-as-
// reverb" send for the lush tail that defines the genre.
//
// Always-on graph:
//   per-note osc -> envelope -> dryWet split:
//     dry -> level -> dest
//     wet -> delay -> level -> dest
//
// Notes themselves are spawned per-step from the Composer.notesForStep()
// so the lead is fully data-driven: change the motif, change the song.

const NOTE_RELEASE_MS = 220;
const VOICE_GAIN = 0.35;

// Sections in which the lead is allowed to sing. Outside these the
// motif lookup will return empty so we don't have to gate explicitly --
// but we still keep the list for clarity and for the level mix to know.
const SINGING_SECTIONS: SectionKind[] = [
  "verse",
  "build",
  "drop",
  "bridge",
  "outro",
];

export class LeadSynth {
  private engine: AudioEngine;
  private beat: ProceduralBeat;
  private composer: Composer | null = null;

  private dryLevel: GainNode | null = null;
  private wetLevel: GainNode | null = null;
  private delay: DelayNode | null = null;
  private feedback: GainNode | null = null;
  private feedbackFilter: BiquadFilterNode | null = null;
  private wetSend: GainNode | null = null;
  private masterLevel: GainNode | null = null;
  private playing = false;
  private targetGain = 0;
  private masterValue = 0;
  private useDelayTail = true;
  private unsubBeat: (() => void) | null = null;
  private unsubBar: (() => void) | null = null;
  private currentSection: SectionKind = "intro";
  private barCount = 0;

  constructor(engine: AudioEngine, beat: ProceduralBeat) {
    this.engine = engine;
    this.beat = beat;
  }

  bindComposer(composer: Composer): void {
    if (this.unsubBar) this.unsubBar();
    this.composer = composer;
    this.unsubBar = composer.onBar.on((ctx) => this.onBar(ctx));
    const cur = composer.getCurrentContext();
    if (cur) this.currentSection = cur.section;
  }

  // Disable the delay-tail "reverb" on low-quality devices to save a
  // handful of nodes per voice and per-frame DSP.
  setDelayTailEnabled(enabled: boolean): void {
    this.useDelayTail = enabled;
    if (this.wetSend && this.engine.context) {
      const t = this.engine.context.currentTime;
      this.wetSend.gain.cancelScheduledValues(t);
      this.wetSend.gain.linearRampToValueAtTime(enabled ? 0.45 : 0, t + 0.2);
    }
  }

  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;

    // Master output for the whole synth.
    this.masterLevel = ctx.createGain();
    this.masterLevel.gain.value = this.masterValue;
    this.masterLevel.connect(dest);

    // Dry path.
    this.dryLevel = ctx.createGain();
    this.dryLevel.gain.value = 1;
    this.dryLevel.connect(this.masterLevel);

    // Delay-as-reverb wet path: a single 3/16th delay with high-feedback
    // through a lowpass to give the tail a darker / soft texture.
    const delaySec = (60 / this.beat.getBpm()) * (3 / 4); // 3/16ths in seconds
    this.delay = ctx.createDelay(2.0);
    this.delay.delayTime.value = delaySec;

    this.feedbackFilter = ctx.createBiquadFilter();
    this.feedbackFilter.type = "lowpass";
    this.feedbackFilter.frequency.value = 2400;

    this.feedback = ctx.createGain();
    this.feedback.gain.value = 0.42;

    this.wetLevel = ctx.createGain();
    this.wetLevel.gain.value = 0.6;

    this.wetSend = ctx.createGain();
    this.wetSend.gain.value = this.useDelayTail ? 0.45 : 0;

    // Wire the feedback loop: send -> delay -> feedback path
    //   wetSend -> delay -> feedbackFilter -> feedback -> delay (loop)
    //   delay   -> wetLevel -> masterLevel
    this.wetSend.connect(this.delay);
    this.delay.connect(this.feedbackFilter);
    this.feedbackFilter.connect(this.feedback);
    this.feedback.connect(this.delay);
    this.delay.connect(this.wetLevel);
    this.wetLevel.connect(this.masterLevel);

    this.unsubBeat = this.beat.onStep.on((s) => this.onBeat(s));
    this.playing = true;
  }

  stop(): void {
    if (!this.playing) return;
    this.unsubBeat?.();
    this.unsubBeat = null;
    if (this.unsubBar) {
      this.unsubBar();
      this.unsubBar = null;
    }
    for (const n of [this.dryLevel, this.wetLevel, this.delay, this.feedback, this.feedbackFilter, this.wetSend, this.masterLevel]) {
      try { n?.disconnect(); } catch { /* gone */ }
    }
    this.dryLevel = null;
    this.wetLevel = null;
    this.delay = null;
    this.feedback = null;
    this.feedbackFilter = null;
    this.wetSend = null;
    this.masterLevel = null;
    this.composer = null;
    this.playing = false;
  }

  setLevel(gain01: number, rampMs = 600): void {
    this.targetGain = Math.max(0, Math.min(1, gain01));
    this.masterValue = this.targetGain;
    if (!this.masterLevel || !this.engine.context) return;
    const t = this.engine.context.currentTime;
    const g = this.masterLevel.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(this.targetGain, t + rampMs / 1000);
  }

  // For backward-compat with code that calls setPitch(rootHz). We
  // ignore it because the lead pitches per-note from the motif.
  setPitch(_rootHz: number): void {
    /* no-op: motif notes carry their own pitch */
  }

  private onBar(ctx: BarContext): void {
    this.currentSection = ctx.section;
    this.barCount += 1;
    // Re-trim the delay tail when BPM changes meaningfully (keeps the
    // tap synced to the song's pulse).
    if (this.delay && this.engine.context) {
      const t = this.engine.context.currentTime;
      const delaySec = (60 / this.beat.getBpm()) * (3 / 4);
      this.delay.delayTime.cancelScheduledValues(t);
      this.delay.delayTime.setTargetAtTime(delaySec, t, 0.05);
    }
  }

  private onBeat(step: BeatStep): void {
    if (!this.dryLevel || !this.composer) return;
    if (this.targetGain <= 0.0001) return;
    if (!SINGING_SECTIONS.includes(this.currentSection)) return;
    const notes = this.composer.notesForStep(step.step);
    if (notes.length === 0) return;
    for (const n of notes) {
      this.scheduleNote(n, step.time);
    }
  }

  private scheduleNote(note: MotifNote, time: number): void {
    const ctx = this.engine.context;
    const composer = this.composer;
    if (!ctx || !composer) return;
    const ctxBar = composer.getCurrentContext();
    if (!ctxBar) return;

    const song = composer.getCurrentSong();
    if (!song) return;
    const semis = SCALES[song.scaleId];
    const n = semis.length;
    const idx = ((note.degree % n) + n) % n;
    const oct = Math.floor(note.degree / n);
    const semitones = semis[idx]! + (oct + (note.octave ?? 0)) * 12;
    // Lead lives ~2 octaves above the song root.
    let leadHz = song.rootHz * Math.pow(2, semitones / 12);
    while (leadHz < 240) leadHz *= 2;
    while (leadHz > 1800) leadHz *= 0.5;

    const velocity = Math.max(0.05, Math.min(1, note.velocity ?? 1));
    const releaseSec =
      ((note.durationSteps ?? 1) * (60 / song.bpm) / 4) +
      NOTE_RELEASE_MS / 1000;

    const saw = ctx.createOscillator();
    saw.type = "sawtooth";
    saw.frequency.setValueAtTime(leadHz, time);

    const sq = ctx.createOscillator();
    sq.type = "square";
    sq.frequency.setValueAtTime(leadHz * 2, time);

    const blend = ctx.createGain();
    blend.gain.value = 0.55;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, time);
    env.gain.linearRampToValueAtTime(VOICE_GAIN * velocity, time + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, time + releaseSec);

    saw.connect(blend);
    sq.connect(blend);
    blend.connect(env);
    // Split into dry + wet sends. Both go through the master.
    if (this.dryLevel) env.connect(this.dryLevel);
    if (this.wetSend) env.connect(this.wetSend);

    saw.start(time);
    sq.start(time);
    saw.stop(time + releaseSec + 0.05);
    sq.stop(time + releaseSec + 0.05);
  }
}
